import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import moment from 'moment-timezone'
import { addMinutes, endOfDay, isToday, startOfDay, subMinutes } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { debounce } from 'lodash'

import {
  getClientOptions as getClientOptionsTrainer,
  getTrainingTypes as getTrainingTypesTrainer,
} from '../Steps/data/trainer.data'
import {
  fetchClients,
  fetchInitialData,
  getClientOptions as getClientOptionsAdmin,
  getEventTypeOptions,
  getTrainerOptions,
} from '../Steps/data/adminManager.data'
import API from '../../../../api/api'
import { selectUserData } from '../../../../reducers/userDataReducer'
import {
  getBaseState,
  getCheckboxHandler,
  getFrequencyOptions,
  getOption,
  getTimezone,
  handleTimeChange,
  transformEventDataToFormState,
  transformFormStateToRequestData,
} from '../helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import TextAreaField from '../../../FormLegacy/FormFieldsV2/TextAreaField'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import Icon from '../../../Icons'
import Loader from '../../../Loader/Loader'
import { socialLinkServiceOptions } from '../../../Organisation/CommunicationPortal/helpers'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import BaseEvent from '../BaseEvent/BaseEvent'
import EditEvent from '../EditEvent/EditEvent'
import EditEventsSchedule from '../EditEventsSchedule/EditEventsSchedule'
import ImagesDatabase from '../ImagesDatabase/ImagesDatabase'
import MediaUploadInput from '../MediaUploadInput/MediaUploadInput'
import MediaUploadInputEditMode from '../MediaUploadInputEditMode/MediaUploadInputEditMode'
import MobilePreview from '../../../EventManagement/common/components/mobilePreview/MobilePreview'
import PDFUpload from '../PDFUpload/PDFUpload'
import PositiveReviewFollowup from '../PositiveReviewFollowup/PositiveReviewFollowup'
import SetupAutomatedReminders from '../SetupAutomatedReminders/SetupAutomatedReminders'

const getAppointmentBaseState = (t) => ({
  eventType: null,
  eventName: '',
  trainer: null,
  client: null,
  description: '',
  location: '',
  date: moment(),
  repeatable: false,
  askForPostEventReview: false,
  automatedReminders: false,
  rsvpd: false,
  rsvpdTime: null,
  nonRsvpd: false,
  nonRsvpdTime: null,
  schedulerType: { value: 'none', label: t('create-event:helpers.frequencyOptions.doesNotRepeat') },
  image: undefined,
  pdf: undefined,
  imagesDatabaseOpen: false,
})

const AppointmentEvent = ({
  goBack,
  userData,
  handleClose,
  editMode,
  eventData,
  repeatable,
  buildingData,
}) => {
  const { t } = useTranslation(['create-event', 'common'])
  const { displaySnackbar } = useSnackbar()
  const [isPopupOpen, togglePopup] = useState(false)
  const [eventsToUpdate, setEventsToUpdate] = useState('false')
  const [fetchState, setFetchState] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isManagerOrAdmin = ['Manager', 'Administrator'].includes(userData.user.role)
  const socialLinkOptions = socialLinkServiceOptions.map((link) => ({
    ...link,
    service: link.value,
    value: link.id,
    label: link.value,
  }))

  useEffect(() => {
    if (isManagerOrAdmin) {
      ;(async () => {
        setFetchState(await fetchInitialData())
      })()
    }
  }, [isManagerOrAdmin])

  const INITIAL_FORM_STATE = editMode
    ? transformEventDataToFormState(eventData)
    : getBaseState(getAppointmentBaseState(t))
  const [formState, setFormState] = useState(INITIAL_FORM_STATE)

  const handleFieldChange = ({ target: { name, value } }) => {
    if (name === 'date') {
      const newFrequencyOptions = getFrequencyOptions(value, t)

      setFormState((prev) => {
        if (prev.schedulerType && prev.schedulerType.value !== 'none') {
          const currentType = prev.schedulerType.value
          const updatedOption = newFrequencyOptions.find((opt) => opt.value === currentType)

          return {
            ...prev,
            [name]: value,
            schedulerType: updatedOption || prev.schedulerType,
          }
        }

        // Otherwise just update the date
        return { ...prev, [name]: value }
      })
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }))
    }
  }

  const onSubmit = async () => {
    setIsSubmitting(true)
    const formData = await transformFormStateToRequestData(
      { ...formState, buildingData },
      'Appointment',
      eventTypeOptions,
      false,
      socialLinkOptions,
    )
    API.createNewEvent(formData)
      .then(() =>
        displaySnackbar(
          t('create-event:appointmentEvent.createdSuccessfully', {
            eventName: formState.eventName,
          }),
        ),
      )
      .catch(() => {
        displaySnackbar(
          t('create-event:appointmentEvent.errorCreating', { eventName: formState.eventName }),
          true,
        )
      })
      .finally(() => {
        setIsSubmitting(false)
        handleClose()
      })
  }

  const handleCheckboxChange = getCheckboxHandler(handleFieldChange)

  const eventTypeOptions = isManagerOrAdmin
    ? fetchState.trainingTypes
      ? getEventTypeOptions(fetchState.trainingTypes)
      : []
    : getTrainingTypesTrainer(userData.services)

  const clientOptions = isManagerOrAdmin
    ? fetchState.clients
      ? getClientOptionsAdmin(fetchState.clients)
      : []
    : getClientOptionsTrainer(userData.clients.slice())

  const trainerOptions =
    isManagerOrAdmin && fetchState.trainers ? getTrainerOptions(fetchState.trainers) : []

  const detailsButtonEnabled =
    !!formState.eventType && !!formState.eventName && !!formState.trainer && !!formState.description

  const timezone = useMemo(
    () => getTimezone(formState, [buildingData]),
    [buildingData, formState.building],
  )

  const { minStartTime, maxStartTime, minEndTime, maxEndTime } = useMemo(() => {
    const selectedDate = new Date(formState.date)

    const minTime = isToday(selectedDate)
      ? toZonedTime(new Date(), timezone)
      : startOfDay(toZonedTime(selectedDate, timezone))
    const maxTime = endOfDay(minTime)

    return {
      minStartTime: minTime,
      maxStartTime: subMinutes(maxTime, 15),
      minEndTime: addMinutes(minTime, 15),
      maxEndTime: maxTime,
    }
  }, [formState.date, timezone])

  useEffect(() => {
    if (editMode && eventData && timezone) {
      setFormState((prev) => ({
        ...prev,
        date: moment(eventData.date).tz(timezone).toDate(),
      }))
    }
  }, [editMode, timezone, eventData])

  useEffect(() => {
    if (editMode && eventData && timezone) {
      setFormState((prev) => ({
        ...prev,
        date: moment(eventData.date).tz(timezone).toDate(),
      }))
    }
  }, [editMode, timezone, eventData])

  const dateAndTimeButtonEnabled =
    !!formState.location && !!formState.date && !!formState.timeStart && !!formState.timeEnd

  const onSubmitEdit = () => {
    if (repeatable) {
      togglePopup(true)
    } else {
      setEventsToUpdate(false)
      editModeConfirmCallback()
    }
  }

  const editModeCancelCallback = () => {
    togglePopup(false)
  }

  const editModeConfirmCallback = async () => {
    setIsSubmitting(true)
    const newData = await transformFormStateToRequestData(
      { ...formState, buildingData },
      'Appointment',
      eventTypeOptions,
      false,
      socialLinkOptions,
    )
    togglePopup(false)
    API.updateEvent(eventData.id, newData, eventsToUpdate)
      .then(() => displaySnackbar(t('create-event:shared.changesSavedSuccessfully')))
      .catch(() => displaySnackbar(t('create-event:appointmentEvent.errorSavingChanges'), true))
      .finally(() => {
        setIsSubmitting(false)
        handleClose()
      })
  }

  const isLoading = !eventTypeOptions.length && !clientOptions.length && !trainerOptions.length

  const loadClientOptions = debounce(async (value, callback) => {
    const response = await fetchClients(0, 0, value)
    const data = response.map((c) => ({
      ...c,
      value: c.id,
      label: `${c.user.firstName} ${c.user.lastName}`,
    }))
    setFetchState((prev) => ({ ...prev, clients: data }))
    callback(data)
  }, 500)

  const config = {
    details: {
      header: t('create-event:baseEvent.steps.eventDetails'),
      canGoNext: detailsButtonEnabled,
      inputs: {
        first: {
          render: () => (
            <Select
              className="select-container"
              classNamePrefix="select-primary"
              name="eventType"
              options={eventTypeOptions}
              placeholder={t('create-event:appointmentEvent.selectAppointmentType')}
              onChange={(e) => setFormState((prev) => ({ ...prev, eventType: e }))}
              value={getOption(formState.eventType, eventTypeOptions)}
              selectStyle={{ width: '100%' }}
            />
          ),
          label: t('create-event:appointmentEvent.selectAppointmentType'),
        },
        second: {
          render: () => (
            <TextField
              name="eventName"
              onChange={handleFieldChange}
              value={formState.eventName}
              placeholder={t('create-event:appointmentEvent.enterAppointmentNamePlaceholder')}
              maxLength={100}
            />
          ),
          label: t('create-event:appointmentEvent.appointmentName'),
        },
        third: isManagerOrAdmin
          ? {
              render: () => (
                <Select
                  className="select-container"
                  classNamePrefix="select-primary"
                  name="trainer"
                  icon="person"
                  options={trainerOptions}
                  placeholder={t('create-event:shared.selectTrainerLabel')}
                  onChange={(e) => setFormState((prev) => ({ ...prev, trainer: e }))}
                  value={getOption(formState.trainer, trainerOptions)}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('create-event:shared.selectTrainerLabel'),
            }
          : null,
        fourth: {
          render: () => (
            <AsyncSelect
              className="select-container"
              classNamePrefix="select-primary"
              defaultOptions={clientOptions}
              cacheOptions
              placeholder={t('create-event:appointmentEvent.selectClient')}
              name="client"
              loadOptions={loadClientOptions}
              value={getOption(formState.client, clientOptions)}
              onChange={(e) => setFormState((prev) => ({ ...prev, client: e }))}
            />
          ),
          label: t('create-event:appointmentEvent.selectClient'),
        },
        fifth: {
          render: () => (
            <TextAreaField
              name="description"
              placeholder={t('create-event:appointmentEvent.appointmentDescriptionPlaceholder')}
              onChange={handleFieldChange}
              value={formState.description}
              maxLength={1000}
            />
          ),
          label: t('create-event:appointmentEvent.appointmentDescription'),
        },
        sixth: null,
      },
    },
    mediaUpload: {
      header: t('create-event:progressTimeline.mediaUpload'),
      canGoNext: !!formState.image,
      editMode: editMode,
      inputs: {
        first: {
          label: formState.imagesDatabaseOpen ? '' : t('create-event:shared.uploadEventPhotoLabel'),
          render: () =>
            editMode ? (
              <MediaUploadInputEditMode
                file={formState.image}
                handleFieldChange={handleFieldChange}
                buttonLabel={t('create-event:shared.replacePhotoButtonLabel')}
                name="image"
                accept="image/*"
                maxFileNameWidth="150px"
              />
            ) : formState.imagesDatabaseOpen ? (
              <ImagesDatabase formState={formState} handleFieldChange={handleFieldChange} />
            ) : (
              <MediaUploadInput
                eventName={formState.eventName}
                formState={formState}
                handleFieldChange={handleFieldChange}
              />
            ),
        },
        image:
          editMode && formState.image
            ? {
                image:
                  typeof formState.image === 'string'
                    ? getAssetLink({
                        path: 'chitown-images/training',
                        file: formState.image,
                        fallback: { file: 'training_image.png' },
                      })
                    : URL.createObjectURL(formState.image),
              }
            : null,
        second: {
          label: t('create-event:shared.uploadPdfOptionalLabel'),
          render: () =>
            editMode ? (
              <MediaUploadInputEditMode
                file={formState.pdf}
                handleFieldChange={handleFieldChange}
                buttonLabel={
                  formState.pdf
                    ? t('create-event:shared.replacePdfButtonLabel')
                    : t('create-event:shared.uploadPdfButtonLabel')
                }
                accept="application/pdf"
                name="pdf"
                maxFileNameWidth={'250px'}
              />
            ) : (
              <PDFUpload handleFieldChange={handleFieldChange} file={formState.pdf} />
            ),
        },
      },
    },
    dateAndTime: {
      header: t('create-event:baseEvent.steps.dateTime'),
      canGoNext: dateAndTimeButtonEnabled,
      inputs: {
        first: {
          render: () => (
            <TextField
              leftIcon={<Icon icon="location2" />}
              name="location"
              placeholder={t('create-event:appointmentEvent.enterAppointmentLocationPlaceholder')}
              onChange={handleFieldChange}
              value={formState.location}
            />
          ),
          label: t('create-event:shared.locationRequiredLabel'),
        },
        second: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              name="date"
              minDate={moment().toDate()}
              value={formState.date}
              onChange={handleFieldChange}
            />
          ),
          label: t('create-event:appointmentEvent.appointmentDateAndTime'),
        },
        third: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minStartTime}
              maxTime={maxStartTime}
              name="timeStart"
              value={formState.timeStart}
              onChange={(e) =>
                handleTimeChange(e, handleFieldChange, formState.timeStart, formState.timeEnd)
              }
            />
          ),
          label: t('create-event:shared.startsLabel'),
        },
        fourth: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minEndTime}
              maxTime={maxEndTime}
              name="timeEnd"
              value={formState.timeEnd}
              onChange={(e) =>
                handleTimeChange(e, handleFieldChange, formState.timeStart, formState.timeEnd)
              }
            />
          ),
          label: t('create-event:shared.endsLabel'),
        },
        fifth: editMode
          ? null
          : {
              render: () => (
                <Select
                  className="select-container"
                  classNamePrefix="select-primary"
                  options={getFrequencyOptions(formState.date, t)}
                  name="schedulerType"
                  placeholder={t('create-event:shared.selectFrequencyPlaceholder')}
                  value={formState.schedulerType}
                  onChange={(e) => setFormState((prev) => ({ ...prev, schedulerType: e }))}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('create-event:shared.frequencyLabel'),
            },
        sixth: null,
        seventh: null,
      },
    },
    summary: {
      header: t('create-event:baseEvent.steps.attendees'),
      canGoNext: !isSubmitting,
      onSubmit: editMode ? onSubmitEdit : onSubmit,
      inputs: {
        first: {
          render: () => (
            <CheckboxField
              name="askForPostEventReview"
              onChange={handleCheckboxChange}
              value={formState.askForPostEventReview}
            />
          ),
          label: t('create-event:shared.askForPostEventReviewLabel'),
          tooltips: t('create-event:shared.askForPostEventReviewTooltip', { returnObjects: true }),
          renderExtra: () =>
            formState.askForPostEventReview && (
              <PositiveReviewFollowup
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
                formState={formState}
                options={socialLinkOptions}
                tooltips={t('create-event:shared.positiveReviewFollowupTooltip', {
                  returnObjects: true,
                })}
              />
            ),
        },
        second: {
          render: () => (
            <CheckboxField
              name="automatedReminders"
              value={formState.automatedReminders}
              onChange={handleCheckboxChange}
            />
          ),
          renderExtra: () =>
            formState.automatedReminders && (
              <SetupAutomatedReminders
                formState={formState}
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
              />
            ),
          label: t('create-event:shared.setupAutomatedRemindersLabel'),
          tooltips: t('create-event:shared.setupAutomatedRemindersTooltip', {
            returnObjects: true,
          }),
        },
        third: null,
        fourth: null,
        fifth: null,
      },
    },
    footer: {
      cta: editMode
        ? t('create-event:shared.saveChangesCta')
        : t('create-event:appointmentEvent.createAnAppointmentCta'),
    },
  }

  return (
    <div className="events-modal">
      <div className="events-modal-card">
        {isLoading ? (
          <Loader style={{ width: '100px', height: '100px', left: '44%', top: '40%' }} />
        ) : editMode ? (
          <EditEvent config={config} eventType="appointment" handleClose={handleClose} />
        ) : (
          <BaseEvent
            config={config}
            goBack={goBack}
            eventType="appointment"
            handleClose={handleClose}
          />
        )}
        <ConfirmPopup
          isOpen={isPopupOpen}
          cancelCallback={editModeCancelCallback}
          confirmCallback={editModeConfirmCallback}
          cancelText={t('common:actions.cancel')}
          confirmText={t('common:actions.confirm')}
          title={t('create-event:shared.cautionTitle')}
          body={
            <EditEventsSchedule
              onChange={(e) => setEventsToUpdate(e.target.value)}
              value={eventsToUpdate}
              formName="appointmentEvent"
            />
          }
        />
      </div>
      <MobilePreview
        formState={formState}
        eventType="Appointment"
        trainerOptions={trainerOptions}
      />
    </div>
  )
}

AppointmentEvent.propTypes = {
  buildingData: PropTypes.object.isRequired,
  editMode: PropTypes.bool,
  eventData: PropTypes.object,
  goBack: PropTypes.func.isRequired,
  handleClose: PropTypes.func,
  repeatable: PropTypes.bool,
  userData: PropTypes.object,
}

export default connect((state) => ({
  userData: selectUserData(state),
  buildingData: selectActiveBuilding(state),
}))(AppointmentEvent)
